import { not, mapBy } from '@ember/object/computed';
import { makeArray } from '@ember/array';
import ArrayProxy from '@ember/array/proxy'; // eslint-disable-line ember/no-proxies
import { computed } from '@ember/object';
import { isPresent, isBlank, isEmpty } from '@ember/utils';
import { copyAttributesForEditing, resetMutableAttributes, materializeAttributes } from '@square/dashboard-data/models/directory/attributes';
import { cleanupProfile } from '@square/dashboard-data/models/directory/contact-profile';
import { copyGroupsForEditing, materializeGroups } from '@square/dashboard-data/models/directory/group';
import Contact, { Profile } from '@square/dashboard-data/models/squareup/rolodex/contact';
import { copyApplicationFieldsForEditing, resetApplicationFields } from '@square/dashboard-data/models/squareup/rolodex/application-fields';
import { parsePhoneNumber } from 'libphonenumber-js';
import { getContactsForPayments } from '@square/dashboard-data/clients/rolodex-contact';

// eslint-disable-next-line ember/no-classic-classes
const RecordMapProxy = ArrayProxy.extend({
  content: computed(function () {
    return [];
  }),
  isLoading: true,
  isLoaded: not('isLoading'),
  hasLoadFailed: false,
  contacts: mapBy('content', 'contact'),
  load(pairs) {
    this.set('content', pairs);
    pairs.map(pair => this.set(pair.get('paymentToken'), pair.get('contact')));
    return this;
  }
});

// TODO: (cren) `prepareForEditing` and `resetChanges` should really be part of the `customer/edit/form-component` component
export function prepareForEditing(contact, schema) {
  contact.inflate();
  copyGroupsForEditing(contact);
  copyAttributesForEditing(schema, contact);
  copyApplicationFieldsForEditing(contact);
}
export function resetChanges(contact) {
  contact.get('profile').revert();
  contact.set('editableGroups', null);
  resetMutableAttributes(contact);
  resetApplicationFields(contact);
}
export function materializeChanges(contact) {
  cleanupProfile(contact.get('profile'));
  materializeGroups(contact);
  materializeAttributes(contact);
}
export function getDisplayName(contact, intl) {
  const displayName = contact.displayName;
  return isPresent(displayName) ? displayName : intl.t('customers.list.listNamePlaceholder');
}

// When countryCode matches number country code, return national format. Otherwise return
// international format.
export function hasPrivateEmailOnly(contact) {
  return isEmpty(contact.profile?.emailAddress) && isPresent(contact.emailId);
}
export function hasPrivatePhoneOnly(contact) {
  return isEmpty(contact.profile?.phoneNumber) && isPresent(contact.phoneId);
}
export function formatPhoneNumber(number, countryCode = 'US') {
  if (!number) {
    return null;
  }
  try {
    const phone = parsePhoneNumber(number, countryCode);
    return phone.country === countryCode ? phone.formatNational() : phone.formatInternational();
  } catch {
    return null;
  }
}
export function getInitials(contact) {
  const givenName = contact.profile?.givenName;
  const surname = contact.profile?.surname;
  const parts = [];
  if (givenName) {
    parts.push(givenName.charAt(0).toUpperCase());
  }
  if (surname) {
    parts.push(surname.charAt(0).toUpperCase());
  }
  return parts.length > 0 ? parts.join('') : null;
}
export default Contact.reopenClass({
  create(params) {
    const contact = this._super(params);
    if (!contact.get('profile')) {
      contact.set('profile', Profile.create());
    }
    return contact;
  },
  fetchForPaymentTokens(params) {
    const records = RecordMapProxy.create();
    records.set('isLoading', true);
    records.set('requestedPaymentTokens', makeArray(params.paymentTokens || params.paymentToken));
    const req = {
      merchantToken: params.merchantToken,
      paymentToken: records.get('requestedPaymentTokens')
    };
    return getContactsForPayments(req).then(resp => {
      let pairs = resp.get('contactPaymentPairs') === undefined ? [] : resp.get('contactPaymentPairs');
      if (params.validContactsOnly) {
        pairs = pairs.filter(pair => pair.get('contact.automatic') === 0 || !isBlank(pair.get('contact.givenName')));
      }
      return records.load(pairs);
    }).catch(() => {
      records.set('hasLoadFailed', false);
      return records;
    }).finally(() => records.set('isLoading', false));
  }
});